import React from "react";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { TreeItem } from "@mui/x-tree-view";
import FolderIcon from "@mui/icons-material/Folder";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import {
  WBS_GROUPS,
  isSoftwareWbs,
  treeGroupKey,
  wbsGroupNodeId,
} from "../utils/wbsGroups";
import { hasDrawings } from "../utils/treeLabels";
import { useTreeViewContext } from "../TreeViewContext";
import { plantNodeId } from "../utils/treeNodeIds";
import {
  PROCUREMENT_CATEGORIES,
  PLACEHOLDER_SECTIONS,
} from "../utils/treeConstants";
import { AddActionIcon, AddActionLabel } from "./AddAction";
import { WbsNode } from "./WbsNode";
import { ProcurementCategoryNode, SectionItemNode } from "./ProcurementNodes";

// The Add WBS button for a plant. Rendered either on the plant's own row
// or, once the list is focused on that plant, up in the header next to the
// breadcrumb - identical either way.
const PlantAddWbsButton = ({ plant }) => {
  const { pickerMode, wbsOperations } = useTreeViewContext();
  if (pickerMode) return null;

  return (
    <IconButton
      size="small"
      title="Add a WBS to this plant"
      onClick={(event) => {
        event.stopPropagation();
        wbsOperations.openAddWbs(plant);
      }}
      sx={{ p: 0.4, borderRadius: 1 }}
    >
      <AddActionIcon color="warning.main" />
      <AddActionLabel>Add WBS</AddActionLabel>
    </IconButton>
  );
};

// A plant's WBS bucketed into one folder per discipline, labeled by type
// code (PIE / PVE / STE / GA / Other), in WBS_GROUPS order.
// Empty disciplines are dropped rather than shown as empty folders.
const WbsDisciplineGroups = ({ plant, visibleWbs }) => {
  const byGroup = new Map();
  visibleWbs.forEach((wbs) => {
    const key = treeGroupKey(wbs);
    if (!byGroup.has(key)) byGroup.set(key, []);
    byGroup.get(key).push(wbs);
  });

  return (
    <>
      {WBS_GROUPS.filter((group) => byGroup.has(group.key)).map((group) => {
        const groupWbs = byGroup.get(group.key);
        const groupId = wbsGroupNodeId(plant.id, group.key);

        return (
          <TreeItem
            key={groupId}
            nodeId={groupId}
            icon={
              <AccountTreeIcon
                fontSize="inherit"
                sx={{ color: "text.disabled" }}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                >
                  {group.label}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  ({groupWbs.length})
                </Typography>
              </Box>
            }
          >
            {groupWbs.map((wbs) => (
              <WbsNode key={wbs.id} plant={plant} wbs={wbs} />
            ))}
          </TreeItem>
        );
      })}
    </>
  );
};

const PlantWbsChildren = ({ plant }) => {
  const { treeData } = useTreeViewContext();
  const wbsList = treeData.wbsByPlant[plant.id];
  const id = plantNodeId(plant.id);

  if (wbsList === "loading") {
    return (
      <TreeItem
        key={`${id}-loading`}
        nodeId={`${id}-loading`}
        label={
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}
          >
            <CircularProgress size={12} />
            <Typography variant="caption" color="text.secondary">
              Loading WBS…
            </Typography>
          </Box>
        }
      />
    );
  }
  if (Array.isArray(wbsList)) {
    // Only WBS types the website creates are listed - legacy / unrecognised
    // types (see isSoftwareWbs) are left out of the tree entirely, unless
    // the WBS carries 2D drawings, which must always stay visible.
    // Showing every subproject for now - filter commented out.
    // const websiteWbs = wbsList.filter(
    //   (wbs) =>
    //     !isSoftwareWbs(wbs) ||
    //     hasDrawings(wbs, treeData.drawingsByWbs),
    // );
    const websiteWbs = wbsList;
    const visibleWbs = websiteWbs.filter((wbs) =>
      treeData.isWbsVisible(plant, wbs),
    );
    if (visibleWbs.length > 0) {
      return <WbsDisciplineGroups plant={plant} visibleWbs={visibleWbs} />;
    }
    return (
      <TreeItem
        key={`${id}-empty`}
        nodeId={`${id}-empty`}
        label={
          <Typography variant="caption" color="text.secondary">
            {websiteWbs.length === 0 ? "No WBS" : "No matches"}
          </Typography>
        }
      />
    );
  }
  return (
    <TreeItem
      key={`${id}-placeholder`}
      nodeId={`${id}-placeholder`}
      label=""
    />
  );
};

export const PlantNode = ({ plant }) => {
  const { activePlantId, pickerMode, nodeTargetRef, plantOperations } =
    useTreeViewContext();
  const { openPlantMenu } = plantOperations;

  const id = plantNodeId(plant.id);
  nodeTargetRef.current[id] = { kind: "plant", plant };

  return (
    <TreeItem
      key={id}
      nodeId={id}
      icon={<FolderIcon fontSize="inherit" sx={{ color: "primary.main" }} />}
      label={
        <Box
          onContextMenu={
            pickerMode ? undefined : (event) => openPlantMenu(event, plant)
          }
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Typography
            variant="body2"
            noWrap
            title={plant.name}
            sx={{
              flex: 1,
              minWidth: 0,
              fontWeight: plant.id === activePlantId ? 700 : 500,
            }}
          >
            {plant.name || "Untitled"}
          </Typography>
          <PlantAddWbsButton plant={plant} />
        </Box>
      }
    >
      {/* A project holds its WBS, its procurement registers and its project
          control entries here. The WBS have no section heading of their own
          - they are the first thing under the project.

          Each section holds its own rows rather than just labelling the ones
          that follow it, so it opens and closes on a click like every other
          folder in the tree, and a project comes up showing its WBS instead
          of a dozen registers under them. */}
      <PlantWbsChildren plant={plant} />
      <TreeItem
        key={`${id}-procurement-section`}
        nodeId={`${id}-procurement-section`}
        className="tree-section-label tree-section-label--procurement"
        label="Procurement"
      >
        {PROCUREMENT_CATEGORIES.map((category) => (
          <ProcurementCategoryNode
            key={category.key}
            plant={plant}
            category={category}
          />
        ))}
      </TreeItem>
      {PLACEHOLDER_SECTIONS.map((section) => (
        <TreeItem
          key={`${id}-${section.key}-section`}
          nodeId={`${id}-${section.key}-section`}
          className={`tree-section-label tree-section-label--${section.key}`}
          label={section.label}
        >
          {section.items.map((item) => (
            <SectionItemNode
              key={item.key}
              plant={plant}
              sectionKey={section.key}
              item={item}
            />
          ))}
        </TreeItem>
      ))}
    </TreeItem>
  );
};
